<template>
  <div class="scim-form mx-4 col-6">
    <b-form-group class="form-group_center" label="Enable SCIM Integration" label-cols="4">
      <b-form-checkbox type="checkbox" v-model="settings.scim_enabled" :disabled="saving" @change="toggleField('scim_enabled')" />
    </b-form-group>
    <template v-if="settings.scim_enabled">
      <b-form-group label="Endpoint URL" label-cols="4">
        <b-input-group>
          <b-input type="text" :value="endpointUrl" disabled />
          <b-input-group-append>
            <b-btn variant="info" @click="copyUrl">
              <i class="fa fa-copy" />
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-form-group label="Authorization Token" label-cols="4">
        <b-input-group>
          <b-input type="text" :value="settings.scim_token" disabled />
          <b-input-group-append>
            <b-btn variant="info" @click="copyToken">
              <i class="fa fa-copy" />
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>
      <b-form-group class="form-group_center" label="Last Sync" label-cols="4">
        {{ lastSyncLabel }}
      </b-form-group>
      <b-form-group class="form-group_center" label="Enable Employees Synchronization" label-cols="4">
        <b-form-checkbox
          type="checkbox"
          v-model="settings.scim_employees_sync"
          :disabled="saving"
          @change="toggleField('scim_employees_sync')"
        />
      </b-form-group>
      <template v-if="settings.scim_employees_sync">
        <b-form-group label="Desk Field Mapping" label-cols="4">
          <b-input
            type="text"
            v-model="settings.scim_desk_field_mapping"
            :disabled="saving"
            placeholder="e.g., customField1 or urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:customField1"
            @blur="saveSettings('scim_desk_field_mapping')"
          />
          <small class="form-text text-muted">
            SCIM custom field name that contains desk number for automatic desk assignment
          </small>
        </b-form-group>
      </template>
    </template>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions, mapMutations, mapState } from 'vuex'

const DISABLE_CONFIRMATIONS_MAP = {
  scim_enabled:
    'Disabling SCIM integration will stop automatic user provisioning and updates.<br>Make sure this action won’t disrupt your workflow.<br>Proceed with caution.',
  scim_employees_sync:
    "Disabling employee record sync will stop automatic updates from SCIM provider.<br>Ensure this won't impact your system before proceeding."
}

export default {
  name: 'Scim',
  data: () => ({
    saving: false,
  }),
  computed: {
    ...mapState('settings', ['scimSettings']),
    endpointUrl() {
      const { host, protocol } = window.location
      return `${protocol}//${host}/api/v1/scim`
    },
    lastSyncLabel() {
      const at = this.settings.scim_synced_at
      return at ? dayjs(at).format('MMM D, YYYY HH:mm') : 'Never'
    },
    settings: {
      get: function() {
        return this.scimSettings
      },
      set: function(settings) {
        this.setScimSettings(settings)
      }
    }
  },
  methods: {
    ...mapMutations('settings', ['setScimSettings']),
    ...mapActions(['loadCommonData']),
    ...mapActions('settings', ['saveScimSettings', 'loadScimSettings']),
    copyToken() {
      if (this.$clipboard(this.settings.scim_token)) {
        this.$appToast.success('Authentication Token copied successfully.')
      } else {
        this.$appToast.error('Error on Authentication Token copy.')
      }
    },
    copyUrl() {
      if (this.$clipboard(this.endpointUrl)) {
        this.$appToast.success('Endpoint URL copied successfully.')
      } else {
        this.$appToast.error('Error on Endpoint URL copy.')
      }
    },
    async toggleField(field) {
      const isDisabling = !this.settings[field]
      let confirmed = true
      if (isDisabling && DISABLE_CONFIRMATIONS_MAP[field]) {
        try {
          confirmed = false
          confirmed = await this.$appToast.question(DISABLE_CONFIRMATIONS_MAP[field])
        } catch (e) {
          if (e.message !== 'canceled') {
            throw e
          }
        }
      }

      if (!confirmed) {
        this.settings[field] = !this.settings[field]
        return
      }

      const done = await this.saveSettings(field)
      if (!done) {
        this.settings[field] = !this.settings[field]
      }
    },
    async saveSettings(field) {
      try {
        this.saving = true
        // store setSettings
        await this.saveScimSettings({[field]: this.settings[field] })
        await this.loadCommonData({ force: true })
        return true
      } catch (e) {
        if (!e.request) {
          throw e
        }
        this.$appToast.error('Failed to Save SCIM Settings!')
        return false
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss">
.form-group_center {
  & > .form-row {
    align-items: center;
  }
}

.scim-form {
  max-width: 800px;
}
</style>
